import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSidebar, SIDEBAR_WIDTH_EXPANDED_PX, SIDEBAR_WIDTH_COLLAPSED_PX } from "../context/SidebarContext";
import { ThemeToggleButton } from "../components/common/ThemeToggleButton";
import NotificationDropdown from "../components/header/NotificationDropdown";
import UserDropdown from "../components/header/UserDropdown";
import { Menu } from "lucide-react";

interface AppHeaderProps {
  title?: string;
}

const routeConfig: Record<string, { title: string }> = {
  "/": { title: "Dashboard" },
  "/outlet/detail": { title: "Outlet Detail" },
  "/profile": { title: "Profile" },
  "/vendor-management/detail": { title: "Detail Vendor" },
  "/vendor-management/employee": { title: "Vendor Employee" },
  "/vendor-management/work-permit": { title: "Vendor Work Permit" },
  "/tenant-relation/announcements": { title: "Announcements" },
  "/tenant-relation/tenant-news": { title: "Tenant News" },
  "/tenant-relation/information": { title: "Information" },
  "/tenantEmployee": { title: "Tenant Employee" },
  "/safetyTask": { title: "Safety Task" },
  "/tenantRequest": { title: "Tenant Request" },
  "/outlet/pestcontrol": { title: "Pest Control" },
  "/sales/revenue-monitor": { title: "Revenue Monitor" },
};

function titleFromPath(pathname: string) {
  const segs = pathname.split("/").filter(Boolean);
  if (segs.length === 0) return "Dashboard";
  const last = segs[segs.length - 1];
  if (!last) return "Dashboard";
  return last.charAt(0).toUpperCase() + last.slice(1);
}

export default function AppHeader({ title }: AppHeaderProps) {
  const { pathname } = useLocation();
  const { isExpanded, toggleMobileSidebar } = useSidebar();
  const [isDesktop, setIsDesktop] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : false
  );

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const currentRoute = routeConfig[pathname];
  const finalTitle = title || currentRoute?.title || titleFromPath(pathname);

  const sidebarWidth = isExpanded ? `${SIDEBAR_WIDTH_EXPANDED_PX}px` : `${SIDEBAR_WIDTH_COLLAPSED_PX}px`;

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-gray-100/70 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:border-gray-800/80 dark:bg-gray-900/75 lg:fixed lg:top-0 transition-all duration-300"
      style={{
        left: isDesktop ? sidebarWidth : "0",
        width: isDesktop ? `calc(100% - ${sidebarWidth})` : "100%",
      }}
    >
      <div className="flex items-center justify-between px-4 sm:px-6 md:px-8 lg:px-12 py-3 sm:py-3 md:py-3.5 lg:py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={toggleMobileSidebar}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Open sidebar"
          >
            <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>

          <h1 className="hidden lg:block text-2xl font-semibold text-gray-900 dark:text-white truncate">
            {finalTitle}
          </h1>
        </div>

        <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3">
          <ThemeToggleButton />
          <NotificationDropdown />
          <UserDropdown />
        </div>
      </div>
    </header>
  );
}